import React, { useState } from "react";
import {
  MessageSquare,
  Send,
  Bot,
  User,
  Loader,
  Image,
  ExternalLink,
} from "lucide-react";
import agentsService from "../../services/agentsService";
import toast from "react-hot-toast";

const AgentTryAITab = ({ agent }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      content: `Hello! I'm ${agent.name}. ${agent.description} Feel free to ask me anything!`,
      timestamp: new Date(),
      mediaUrl: null,
      metadata: null,
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: inputMessage.trim(),
      timestamp: new Date(),
      mediaUrl: null,
      metadata: null,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const response = await agentsService.testAgent(
        agent.id,
        inputMessage.trim()
      );

      // Parse the response properly
      const botResponse = response.response || {};
      const messageContent =
        botResponse.message ||
        response.message ||
        "I received your message but had trouble generating a response.";

      const botMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: messageContent,
        mediaUrl: botResponse.media_url || null,
        timestamp: new Date(),
        metadata: {
          action: botResponse.action,
          topicsCovered: botResponse.topics_covered || [],
          informationProvided: botResponse.information_provided || [],
          cost: botResponse.cost || 0,
          escalationReason: botResponse.escalation_reason,
          modelUsed: botResponse.model_used,
          contextAnalysis: botResponse.context_analysis,
        },
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error testing agent:", error);
      toast.error("Failed to send message to agent");

      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content:
          "Sorry, I encountered an error while processing your message. Please try again.",
        timestamp: new Date(),
        mediaUrl: null,
        metadata: null,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderMediaContent = (mediaUrl) => {
    if (!mediaUrl) return null;

    return (
      <div className="mt-2">
        <div className="relative inline-block">
          <img
            src={mediaUrl}
            alt="Shared content"
            className="max-w-xs rounded-lg shadow-sm"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <div className="hidden bg-gray-100 dark:bg-gray-700 p-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
            <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
              <Image className="w-4 h-4" />
              <span className="text-sm">Image preview unavailable</span>
              <a
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-600 ml-2"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMetadata = (metadata) => {
    if (
      !metadata ||
      (!metadata.topicsCovered?.length &&
        !metadata.informationProvided?.length &&
        !metadata.cost)
    )
      return null;

    return (
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {metadata.topicsCovered?.length > 0 && (
          <div>Topics: {metadata.topicsCovered.join(", ")}</div>
        )}
        {metadata.informationProvided?.length > 0 && (
          <div>Info provided: {metadata.informationProvided.join(", ")}</div>
        )}
        {metadata.cost > 0 && <div>Cost: ${metadata.cost.toFixed(6)}</div>}
        {metadata.escalationReason && (
          <div className="text-yellow-600 dark:text-yellow-400">
            Escalation: {metadata.escalationReason}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-[600px] flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg">
            <MessageSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Test Your Agent
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Chat with {agent.name} to test its knowledge and responses.
            </p>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start space-x-3 ${
              message.type === "user" ? "flex-row-reverse space-x-reverse" : ""
            }`}
          >
            {/* Avatar */}
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.type === "user"
                  ? "bg-blue-100 dark:bg-blue-900/20"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              {message.type === "user" ? (
                <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              ) : (
                <Bot className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              )}
            </div>

            {/* Message */}
            <div
              className={`flex-1 max-w-xs md:max-w-md lg:max-w-lg ${
                message.type === "user" ? "text-right" : "text-left"
              }`}
            >
              <div
                className={`inline-block px-4 py-2 rounded-lg ${
                  message.type === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                {/* Media content for bot messages */}
                {message.type === "bot" && message.mediaUrl && (
                  <div className="mt-2">
                    {renderMediaContent(message.mediaUrl)}
                  </div>
                )}
              </div>

              {/* Metadata for bot messages */}
              {message.type === "bot" && message.metadata && (
                <div className="mt-1">{renderMetadata(message.metadata)}</div>
              )}

              <p
                className={`text-xs text-gray-500 dark:text-gray-400 mt-1 ${
                  message.type === "user" ? "text-right" : "text-left"
                }`}
              >
                {formatTime(message.timestamp)}
              </p>
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <Bot className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="flex-1">
              <div className="inline-block px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                <div className="flex items-center space-x-2">
                  <Loader className="w-4 h-4 animate-spin text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {agent.name} is typing...
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="p-6 border-t border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSendMessage} className="flex space-x-3">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type your message..."
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!inputMessage.trim() || isLoading}
            className="flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>

        {/* Agent Info */}
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <div>
              <span className="font-medium">Model:</span> {agent.model_provider}{" "}
              - {agent.model_name}
              {agent.hands_off_type && (
                <span className="ml-4 inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                  Hands-off Mode
                </span>
              )}
            </div>

            {agent.behavior_rules && (
              <div>
                <span className="font-medium">Behavior:</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  {agent.behavior_rules.substring(0, 100)}
                  {agent.behavior_rules.length > 100 ? "..." : ""}
                </span>
              </div>
            )}

            <div className="text-xs text-blue-600 dark:text-blue-400">
              ✨ Responses may include images, links, and cost information
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentTryAITab;
